import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getPracticeExam, submitPracticeExam } from '../api';
import './PracticeTestPage.css';

export default function PracticeTestPage() {
  const { stateName, gradeName } = useParams();
  const grade = Number((gradeName || '').match(/\d+/)?.[0]);
  const supported = (stateName || '').toLowerCase() === 'massachusetts' && grade > 0;
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const back = `/state/${encodeURIComponent(stateName || '')}/grade/${encodeURIComponent(gradeName || '')}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setExam(null);
    setAnswers({});
    setResults(null);
    setError('');
    if (!supported) {
      setError('MCAS practice tests are available for Massachusetts grades.');
      setLoading(false);
      return undefined;
    }
    getPracticeExam(grade).then((data) => {
      if (!cancelled) setExam(data);
    }).catch((err) => {
      if (!cancelled) setError(err.message || 'Unable to load the practice test.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [grade, supported, retry]);

  const answered = exam?.questions.filter((q) => (answers[q.number] || '').trim()).length || 0;
  const totalPoints = exam?.questions.reduce((sum, q) => sum + q.points, 0) || 0;
  const earned = results?.reduce((sum, r) => sum + r.earnedPoints, 0) || 0;

  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const data = await submitPracticeExam(grade, answers);
      setResults(data.results);
    } catch (err) {
      setError(err.message || 'Unable to submit. Your answers are still here.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="browse-page-container practice-test-page">
      <Link className="back-button" to={back}>← Back to {gradeName}</Link>
      {loading && <p role="status">Loading practice test…</p>}
      {error && <div className="error" role="alert">{error}</div>}
      {!loading && !exam && supported && (
        <button className="test-primary" onClick={() => setRetry((n) => n + 1)}>Try again</button>
      )}
      {exam && <>
        <header className="browse-page-header">
          <h1 className="browse-page-title">{exam.title}</h1>
          <p className="test-text">{exam.instructions}</p>
          <p>{exam.questions.length} questions · {totalPoints} points · Unofficial practice</p>
          <p>Answers stay on this page until you leave or reload. Written responses are graded by AI using the scoring rubric.</p>
        </header>
        {results && <section className="test-summary" aria-live="polite">
          <h2>Your practice score</h2>
          <p>{earned} / {totalPoints} points</p>
          <p>Review your feedback, worked solutions, and scoring rubrics below.</p>
          <button type="button" className="test-primary" onClick={() => {
            setAnswers({}); setResults(null); setError('');
          }}>Try the test again</button>
        </section>}
        <form onSubmit={submit}>
          {!results && <p role="status">{answered} of {exam.questions.length} answered</p>}
          {exam.questions.map((q, index) => {
            const result = results?.[index];
            return <section className="test-question" key={q.number}>
              <h2>Question {q.number} <span>({q.points} {q.points === 1 ? 'point' : 'points'})</span></h2>
              <p className="test-topic">{q.topic}</p>
              <div className="test-text" id={`question-${q.number}`}>{q.question}</div>
              {q.choices.length > 0 ? <fieldset disabled={Boolean(results) || submitting}>
                <legend>Select an answer for question {q.number}</legend>
                {q.choices.map((choice, choiceIndex) => <label className="test-choice" key={choiceIndex}>
                  <input type="radio" name={`answer-${q.number}`} value={choice}
                    checked={answers[q.number] === choice}
                    onChange={() => setAnswers((prev) => ({ ...prev, [q.number]: choice }))} />
                  <span>{choice}</span>
                </label>)}
              </fieldset> : <div className="test-written">
                <label htmlFor={`answer-${q.number}`}>Your answer and work</label>
                <textarea id={`answer-${q.number}`} rows={4} disabled={Boolean(results) || submitting}
                  aria-describedby={`question-${q.number}`} value={answers[q.number] || ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.number]: e.target.value }))} />
              </div>}
              {result && <div className="test-review">
                <strong>{result.earnedPoints === result.points ? 'Correct' : result.earnedPoints > 0 ? 'Partial credit' : 'Incorrect or unanswered'} · {result.earnedPoints} / {result.points} points</strong>
                {result.gradingMethod === 'openai' && <p className="test-topic">AI graded</p>}
                {result.feedback && <p className="test-text"><b>Feedback:</b> {result.feedback}</p>}
                <p className="test-text"><b>Answer:</b> {result.answer}</p>
                <p className="test-text">{result.solution}</p>
                <p className="test-text"><b>Scoring:</b> {result.rubric}</p>
              </div>}
            </section>;
          })}
          {!results && <div className="test-submit">
            {answered < exam.questions.length && <p>{exam.questions.length - answered} unanswered questions. You can still submit.</p>}
            <button className="test-primary" type="submit" disabled={submitting}>
              {submitting ? 'Grading your answers…' : 'Submit and review answers'}
            </button>
          </div>}
        </form>
      </>}
    </main>
  );
}
